import { Timestamp, TimestampOutdated } from 'aos-ds/src/icons/actions'
import type { FigmaGlyph } from 'aos-ds/src/icons/glyph'
import { Icon } from 'aos-ds/src/icons/Icon'
import {
    type ContractVariant,
    timestampContract,
} from 'aos-ds/src/patterns/timestamp/timestamp.contract'
import { Tooltip as TooltipSurface } from 'aos-ds/src/primitives/tooltip/Tooltip'
import { Typography } from 'aos-ds/src/typography/Typography'
import React, { type ReactNode } from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { Pressable } from '../pressable'
import { Tooltip, TooltipContent } from '../tooltip'

export const TIMESTAMP_VARIANTS = timestampContract.props.variant.values

export type TimeStampVariant = (typeof TIMESTAMP_VARIANTS)[number]

const GLYPHS: Record<string, FigmaGlyph> = { Timestamp, TimestampOutdated }

export const timeStampVariantKey = (variant: TimeStampVariant): string => `variant=${variant}`

export const resolveTimeStamp = (variant: TimeStampVariant): ContractVariant => {
    const key = timeStampVariantKey(variant)
    const cell = (timestampContract.variants as Record<string, ContractVariant>)[key]
    if (!cell) {
        throw new Error(`TimeStamp: the Figma contract has no "${key}"`)
    }
    return cell
}

export const resolveTimeStampGlyph = (variant: TimeStampVariant): FigmaGlyph => {
    const cell = resolveTimeStamp(variant)
    const name = cell.composes[0]?.component
    const glyph = name ? GLYPHS[name] : undefined
    if (!glyph) {
        throw new Error(
            `TimeStamp: no icon glyph for "${name}" in "${timeStampVariantKey(variant)}"`,
        )
    }
    return glyph
}

const TIME_TEXT_VARIANT = 'p2-regular'
const TIME_TEXT_TONE = 'muted'

const TOOLTIP_CONTENT_RESET = 'p-0 rounded-none bg-transparent'

export type TimeStampProps = {
    variant?: TimeStampVariant
    tooltip?: ReactNode
    time?: string
    className?: string
    testID?: string
}

export const TimeStamp = ({
    variant = 'default',
    tooltip,
    time,
    className,
    testID = 'timestamp',
}: TimeStampProps) => {
    const cell = resolveTimeStamp(variant)
    const glyph = resolveTimeStampGlyph(variant)

    const mark = (
        <View className={cell.container} testID={`${testID}-icon`}>
            <Icon glyph={glyph} className={cell.icon} />
        </View>
    )

    const content = (
        <View className={twMerge('flex-row items-center', className)} testID={testID}>
            {mark}
            {time !== undefined && (
                <Typography
                    variant={TIME_TEXT_VARIANT}
                    tone={TIME_TEXT_TONE}
                    testID={`${testID}-time`}
                >
                    {time}
                </Typography>
            )}
        </View>
    )

    if (tooltip === undefined) {
        return content
    }

    return (
        <Tooltip
            trigger={triggerProps => (
                <Pressable
                    {...triggerProps}
                    onHoverIn={triggerProps.onMouseEnter}
                    onHoverOut={triggerProps.onMouseLeave}
                    accessibilityLabel={typeof tooltip === 'string' ? tooltip : undefined}
                    className='self-start web:pointer-events-auto web:cursor-default'
                >
                    {content}
                </Pressable>
            )}
        >
            <TooltipContent className={TOOLTIP_CONTENT_RESET}>
                <TooltipSurface variant='Simple' testID={`${testID}-tooltip`}>
                    {tooltip}
                </TooltipSurface>
            </TooltipContent>
        </Tooltip>
    )
}

TimeStamp.displayName = 'TimeStamp'
